import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
// biome-ignore lint/style/noRestrictedImports: this component uses global SCSS, not CSS modules
import classNames from 'classnames';
import { ChangeEvent, useEffect, useState } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import usePlacesAutocomplete, { PlacePrediction } from '../../hooks/usePlacesAutocomplete';
import type { TripCard, TripList } from '../../redux/trip/trip.types';
import { isPlaceAlreadyInList } from '../../redux/trip/trip.utils';
import './PlacesAutocomplete.scss';

type PlacesAutocompleteProps = {
  location?: { lat: number; lng: number };
  radius?: number;
  types?: string[];
  placeholder?: string;
  className?: string;
  onSuggestionMouseEnter?: (suggestion: PlacePrediction) => void;
  onSuggestionMouseLeave?: (suggestion: PlacePrediction) => void;
  onSuggestionSelect?: (
    suggestion: PlacePrediction,
    sessionToken: string,
    usedNewApi: boolean
  ) => void;
  setIsAddingPlace?: (value: boolean) => void;
  shouldClearValueOnSelect?: boolean;
  isMobilePlayground?: boolean;
  isHomeInput?: boolean;
  hasError?: boolean;
  activeList?: TripList;
  cards?: { [key: string]: TripCard };
  showIcon?: boolean;
  showSearchIcon?: boolean;
  onInputChange?: (value: string) => void;
};

function PlacesAutocomplete({
  location = { lat: 40.7127753, lng: -74.0059728 },
  radius = 10000,
  types,
  placeholder,
  className,
  onSuggestionMouseEnter,
  onSuggestionMouseLeave,
  onSuggestionSelect,
  setIsAddingPlace,
  shouldClearValueOnSelect,
  isMobilePlayground,
  isHomeInput,
  hasError,
  activeList,
  cards,
  showIcon = false,
  showSearchIcon = false,
  onInputChange,
}: PlacesAutocompleteProps) {
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
    refreshSessionToken,
    sessionToken,
    usedNewApiForAutocomplete,
  } = usePlacesAutocomplete({
    requestOptions: {
      location: location ? new window.google.maps.LatLng(location.lat, location.lng) : undefined,
      radius,
      types,
    },
    debounce: 300,
  });
  const [clearInputOnClickAway, setClearInputOnClickAway] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally re-runs when a new response arrives
  useEffect(() => {
    setIsTyping(false);
  }, [status, data]);

  const ref = useOnclickOutside(() => {
    // When user clicks outside of the component, we can dismiss
    // the searched suggestions by calling this method
    clearSuggestions();
    if (clearInputOnClickAway) {
      setValue('');
    }
  });

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    onInputChange?.(e.target.value);
    setIsTyping(true);
    setHasSelected(false);
  };

  const handleSelect = (suggestion: PlacePrediction) => () => {
    setValue(shouldClearValueOnSelect ? '' : suggestion.description, false);
    clearSuggestions();
    setHasSelected(true);

    if (onSuggestionSelect) {
      setClearInputOnClickAway(false);
      onSuggestionSelect(suggestion, sessionToken, usedNewApiForAutocomplete);
      refreshSessionToken();
      if (setIsAddingPlace) {
        setIsAddingPlace(false);
      }
    }
  };

  const handleMouseEnter = (suggestion: PlacePrediction) => () => {
    if (onSuggestionMouseEnter) {
      onSuggestionMouseEnter(suggestion);
    }
  };

  const handleMouseLeave = (suggestion: PlacePrediction) => () => {
    if (onSuggestionMouseLeave) {
      onSuggestionMouseLeave(suggestion);
    }
  };

  const handleCancelInput = () => {
    setValue('');
    clearSuggestions();
    setIsAddingPlace?.(false);
  };

  const handleClearInput = () => {
    setValue('');
    onInputChange?.('');
    clearSuggestions();
  };

  const renderAddedBadge = (googlePlaceId: string) => {
    if (!activeList || !cards) {
      return null;
    }
    if (!isPlaceAlreadyInList(activeList, cards, googlePlaceId)) {
      return null;
    }

    return <div className='already-added-badge'>✓ Already added</div>;
  };

  const renderNoResults = () => (
    <div className={className}>
      <span className='no-results-msg'>No results found for "{value}"</span>
    </div>
  );

  const renderSuggestions = () => (
    <div className={classNames('places-autocomplete-suggestions-container', className)}>
      {data.map((suggestion) => {
        const { place_id } = suggestion;

        return (
          <div
            key={place_id}
            className='places-autocomplete-suggestion'
            onMouseEnter={handleMouseEnter(suggestion)}
            onMouseLeave={handleMouseLeave(suggestion)}
            onClick={handleSelect(suggestion)}
          >
            <div className='place-suggestion-content'>
              <span className='place-name'>
                {suggestion.structured_formatting?.main_text || suggestion.description}
              </span>
              <span className='place-address'>
                {suggestion.structured_formatting?.secondary_text || ''}
              </span>
              {renderAddedBadge(suggestion.place_id)}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div
      ref={ref}
      className={classNames('places-autocomplete-container', className)}
    >
      <div>
        {showIcon && (
          <img
            className='places-autocomplete-input-icon'
            src='/svgs/marker.svg'
            alt='marker'
          />
        )}
        {showSearchIcon && <SearchRoundedIcon className='places-autocomplete-search-icon' />}
        <input
          className={classNames(
            'places-autocomplete-input',
            className,
            hasError && 'places-autocomplete-input--error'
          )}
          value={value}
          onChange={handleInput}
          disabled={!ready && !isMobilePlayground}
          placeholder={placeholder}
          autoFocus={isMobilePlayground}
        />
      </div>
      {isMobilePlayground && (
        <ArrowBackIcon
          className='places-autocomplete-cancel-btn'
          onClick={handleCancelInput}
        />
      )}
      {value.length !== 0 && !isHomeInput && (
        <HighlightOffIcon
          className='places-autocomplete-clear-btn'
          onClick={handleClearInput}
        />
      )}
      {status === 'OK' && renderSuggestions()}
      {status === 'ZERO_RESULTS' && !isTyping && !hasSelected && value.trim() && renderNoResults()}
    </div>
  );
}

PlacesAutocomplete.displayName = 'PlacesAutocomplete';
export default PlacesAutocomplete;
